import { get } from 'svelte/store';
import {
	createVirtualDevice,
	deleteVirtualDevice,
	getComponentByDevice,
	getDeviceIDByComponent,
} from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { devices } from 'src/stores/lists/devices';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { isIRController } from './interface_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';

export const IR_CONTROLLER_TYPE = 'S4CC-0D1XX1WW1';
export const IR_DEVICE_TYPE = 'ir-device';
export const IR_CONTROLLER_CATEGORY = 'ir_controller';
export const IR_DEVICE_KEY = /^irdevice:2\d{2}$/;
export const IR_CODE_KEY = /^ircode:2\d{2}$/;
export const IR_CODE_NAME_EXISTS_ERROR = -103;

export type IRCode = {
	key: string;
	id: number;
	name: string;
	device_id?: number;
};

export type IRDeviceItem = {
	key: string;
	id: number;
	name: string;
	codes: IRCode[];
};

export const isIRDevice = (deviceType?: string): boolean => /^(ir-device)$/.test(deviceType || '');

export const isIRControllerCategory = (device: Pick<Device, 'category'>): boolean =>
	device?.category === IR_CONTROLLER_CATEGORY;

const getComponentId = (key: string, config?: { id?: number }): number =>
	config?.id ?? Number(key.split(':')[1]);

export const getIRCodes = (settings: Record<string, any> | undefined, irDeviceId?: number): IRCode[] =>
	Object.entries(settings || {})
		.filter(([key]) => IR_CODE_KEY.test(key))
		.map(([key, config]: [string, { id?: number; name?: string; device_id?: number }]) => ({
			key,
			id: getComponentId(key, config),
			name: config?.name || key,
			device_id: config?.device_id,
		}))
		.filter((code) => irDeviceId === undefined || code.device_id === irDeviceId);

export const getIRDevices = (settings: Record<string, any> | undefined): IRDeviceItem[] => {
	const irCodes = getIRCodes(settings);
	return Object.entries(settings || {})
		.filter(([key]) => IR_DEVICE_KEY.test(key))
		.map(([key, config]: [string, { id?: number; name?: string }]) => {
			const id = getComponentId(key, config);
			return {
				key,
				id,
				name: config?.name || key,
				codes: irCodes.filter((code) => code.device_id === id),
			};
		});
};

export const getIRDeviceName = (
	settings: Record<string, any> | undefined,
	irDeviceId?: number,
): string => (irDeviceId === undefined ? '' : settings?.[`irdevice:${irDeviceId}`]?.name || '');

export const getIRFirmwareId = (channel: number): number => channel - 3000;

export const getIRChildDeviceId = (parentId: string, irDeviceId: number): string =>
	getDeviceIDByComponent(parentId, `irdevice:${irDeviceId}`);

export const addIRDevice = (device: Device, name: string): Promise<SettingRequest> =>
	gen2Request(device, 'IR.AddDevice', { config: { name } });

export const deleteIRDevice = (device: Device): Promise<SettingRequest> => {
	const component = getComponentByDevice(device);
	const config = devices_settings.getByRealId(device.id)?.[component];
	return gen2Request(device, 'IR.DeleteDevice', {
		id: getComponentId(component, config),
	});
};

export const setIRDeviceName = (device: Device, id: number, name: string): Promise<SettingRequest> =>
	gen2Request(device, 'IRDevice.SetConfig', { id, config: { name } });

export const emitIRCode = (device: Device, id: number, repeats = 1): Promise<SettingRequest> =>
	gen2Request(device, 'IRCode.Emit', { id, repeats });

export const learnIRCode = (
	device: Device,
	id: number,
	name: string,
	timeout: number,
): Promise<SettingRequest> => gen2Request(device, 'IRDevice.LearnCode', { id, name, timeout });

export const deleteIRCode = (device: Device, id: number, name: string): Promise<SettingRequest> =>
	gen2Request(device, 'IRDevice.DeleteCode', { id, name });

export const setIRCodeName = (
	device: Device,
	id: number,
	name: string,
	irDeviceId: number,
): Promise<SettingRequest> =>
	gen2Request(device, 'IRCode.SetConfig', {
		id,
		config: { name, device_id: irDeviceId },
	});

export const syncIRDevicesByParent = async (
	deviceId: Device['id'],
	deviceSettings: Record<string, any>,
) => {
	const allDevices = get(devices);
	const parentDevice = allDevices[deviceId];
	if (!parentDevice) return;

	const irComponents = Object.keys(deviceSettings || {}).filter((key) => IR_DEVICE_KEY.test(key));
	const expectedChildIds = new Set(
		irComponents.map((component) => getDeviceIDByComponent(deviceId, component)),
	);
	const reportedComponents = new Set(devices_status.getByRealId(deviceId)?.vcomps || []);

	for (const component of irComponents) {
		const childDeviceId = getDeviceIDByComponent(deviceId, component);
		if (!allDevices[childDeviceId]) {
			await createVirtualDevice(parentDevice, deviceSettings[component], component);
		}
	}

	for (const childDevice of Object.values(allDevices)) {
		if (
			isIRDevice(childDevice.type) &&
			childDevice.id.startsWith(`${deviceId}_`) &&
			!expectedChildIds.has(childDevice.id) &&
			!reportedComponents.has(`irdevice:${getIRFirmwareId(childDevice.channel)}`)
		) {
			await deleteVirtualDevice(childDevice);
		}
	}
};

devices_settings.subscribe((settings) => {
	for (const dev in settings) {
		const deviceType = settings[dev]?.device?.type;
		if (isIRController(deviceType)) {
			syncIRDevicesByParent(dev, settings[dev]);
		}
	}
});
